import {
  Agent,
  AgentInput,
  AgentLicenses,
  AgentSummary,
  CreatePasswordInput,
  InviteInput,
  PaginatedAgents,
  UpdatePasswordInput
} from 'types/AgentTypes';
import useHttp from '../helpers/HttpMethods';

const http = useHttp();

class AgentsService {
  /**
   * Busca os agentes paginados.
   * @param {string} filter - Filtros em string.
   * @returns {Promise<PaginatedAgents | Response>} - Agentes em formato paginado.
   */
  static async list(filter: string): Promise<PaginatedAgents | Response> {
    return await http.get(`/users?${filter}`);
  }

  /**
   * Busca dados resumidos dos agentes.
   * @returns {Promise<AgentSummary[] | Response>} - Lista de agentes resumidos.
   */
  static async listSummaryData(): Promise<AgentSummary[] | Response> {
    return await http.get('/users/summary-data');
  }

  /**
   * Visualiza as licenças disponíveis.
   * @returns {Promise<AgentLicenses | Response>} - Informações sobre licenças.
   */
  static async viewLicenses(): Promise<AgentLicenses | Response> {
    return await http.get('/users/licenses');
  }

  /**
   * Cria um novo agente.
   * @param {AgentInput} body - Dados do agente a ser criado.
   * @returns {Promise<Agent | Response>} - Agente criado ou resposta de erro.
   */
  static async create(body: AgentInput): Promise<Agent | Response> {
    return await http.post(body, '/users');
  }

  /**
   * Edita um agente existente.
   * @param {number} id - ID do agente a ser atualizado.
   * @param {AgentInput} body - Dados do agente para atualização.
   * @returns {Promise<Agent | Response>} - Agente atualizado ou resposta de erro.
   */
  static async edit(id: number, body: AgentInput): Promise<Agent | Response> {
    return await http.put(body, `/users/${id}`);
  }

  /**
   * Atualiza a senha de um agente.
   * @param {number} id - ID do agente.
   * @param {UpdatePasswordInput} body - Dados para atualização de senha.
   * @returns {Promise<Response>} - Retorno padrão de uma request.
   */
  static async updatePassword(
    id: number,
    body: UpdatePasswordInput
  ): Promise<Response> {
    return await http.put(body, `/users/${id}/update-password`);
  }

  /**
   * Envia email de reset de senha para um agente.
   * @param {number} id - ID do agente.
   * @returns {Promise<Response>} - Retorno padrão de uma request.
   */
  static async resetPassword(id: number): Promise<Response> {
    return await http.post({}, `/users/${id}/reset-password`);
  }

  /**
   * Cria senha para um agente (usado no primeiro acesso).
   * @param {CreatePasswordInput} body - Dados para criação de senha.
   * @returns {Promise<Response>} - Retorno padrão de uma request.
   */
  static async createPassword(body: CreatePasswordInput): Promise<Response> {
    return await http.postWithNoAuth(body, '/users/create-password');
  }

  /**
   * Envia convite para um agente.
   * @param {InviteInput} body - Dados do convite.
   * @returns {Promise<Response>} - Retorno padrão de uma request.
   */
  static async invite(body: InviteInput): Promise<Response> {
    return await http.postWithNoAuth(body, '/users/invite');
  }

  /**
   * Busca o perfil do agente autenticado.
   * @returns {Promise<Agent | Response>} - Dados do agente autenticado.
   */
  static async myProfile(): Promise<Agent | Response> {
    return await http.get('/users/my-profile');
  }

  /**
   * Busca o agente autenticado.
   * @returns {Promise<AgentSummary | Response>} - Dados do agente autenticado.
   */
  static async getAuthenticatedUser(): Promise<AgentSummary | Response> {
    return await http.get('/users/authenticated-user');
  }

  /**
   * Busca a assinatura de um usuário específico ou do usuário autenticado se nenhum ID for fornecido.
   * @param {number} [id] - ID do usuário (opcional).
   * @returns {Promise<{ signature: string } | Response>} - Assinatura do usuário.
   */
  static async getSignature(
    id?: number
  ): Promise<{ signature: string } | Response> {
    const url = id ? `/users/${id}/signature` : '/users/me/signature';
    return await http.get(url);
  }

  /**
   * Atualiza o perfil do agente autenticado.
   * @param {any} body - Dados do perfil a serem atualizados.
   * @returns {Promise<Response>} - Retorno padrão de uma request.
   */
  static async updateMyProfile(body: any): Promise<Response> {
    return await http.put(body, '/users/my-profile');
  }

  /**
   * Atualiza a assinatura do agente autenticado.
   * @param {{ signature: string }} body - Assinatura.
   * @returns {Promise<Response>} - Retorno padrão de uma request.
   */
  static async updateMySignature(body: {
    signature: string;
  }): Promise<Response> {
    return await http.put(body, '/users/me/signature');
  }
}

export default AgentsService;
